import { STRINGS } from 'config';
import { Vitals } from 'store/reducer/snomed';

const {
  INTEROP: { NAME: { EMIS, S1 }, SNOMED: { EMIS_BMI_UNIT, S1_BMI_UNIT } },
} = STRINGS;

export interface SnomedEntry {
    [x: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    code: string;
    info: string;
    unit: string | null;
    value: string | null;
}

// config this?
const vitalCodeMap: Record<keyof Vitals, Omit<SnomedEntry, 'value'>> = {
  systolic: {
    code: '407554009',
    info: 'Sitting systolic blood pressure (observable entity)',
    unit: 'mmHg',
  },
  diastolic: {
    code: '407555005',
    info: 'Sitting diastolic blood pressure (observable entity)',
    unit: 'mmHg',
  },
  height: {
    code: '248333004',
    info: 'Standing height (observable entity)',
    unit: 'm',
  },
  weight: {
    code: '27113001',
    info: 'Body weight (observable entity)',
    unit: 'kg',
  },
  pulseRate: {
    code: '78564009',
    info: 'Heart rate measured at systemic artery (observable entity)',
    unit: 'bpm',
  },
  tympanicTemperature: {
    code: '415974002',
    info: 'Core body temperature measured at tympanic membrane (observable entity)',
    unit: 'C',
  },
  peakFlowRate: {
    code: '18491006',
    info: 'Peak expiratory flow rate (observable entity)',
    unit: 'L/min',
  },
  bmi: {
    code: '60621009',
    info: 'Body mass index (observable entity)',
    unit: null,
  },
  pulseRhythm: {
    code: '',
    info: '',
    unit: null,
  },
};

export const pulseRhythmCodes = {
  162999005: 'On examination - pulse rhythm regular (finding)',
  163001005: 'On examination - pulse rhythm regularly irregular (finding)',
  163000006: 'On examination - pulse rhythm irregularly irregular (finding)',
};

export function convertVitalsToEntries(vitals: Vitals, clinicalSystemName: string | null): SnomedEntry[] {
  const out: SnomedEntry[] = [];
  const { diastolic, systolic } = vitals;
  const includeBP = systolic != null && diastolic != null;

  (Object.entries(vitalCodeMap) as Array<[keyof Vitals, Omit<SnomedEntry, 'value'>]>).forEach(
    ([key, { code, info, unit }]) => {
      if ((key === 'systolic' || key === 'diastolic') && !includeBP) {
        return;
      }

      if (key === 'pulseRhythm') {
        const v = vitals[key];
        if (v != null) {
          const pulseRhythmInfo = pulseRhythmCodes[v as unknown as keyof typeof pulseRhythmCodes];
          if (pulseRhythmInfo) {
            out.push({
              code: v,
              info: pulseRhythmInfo,
              unit: null,
              value: null,
            });
          }
        }
        return;
      }

      if (key === 'bmi') {
        const v = vitals[key];
        const isEmis = clinicalSystemName === EMIS;
        const isS1 = clinicalSystemName === S1;
        const bmiUnit = (isEmis && EMIS_BMI_UNIT) || (isS1 && S1_BMI_UNIT) || null;
        if (v != null) {
          out.push({ code, info, unit: bmiUnit, value: String(v) });
        }
        return;
      }

      const v = vitals[key];
      if (v != null) {
        out.push({ code, info, unit, value: String(v) });
      }
    },
  );

  return out;
}

export { vitalCodeMap };
